import {
  BAKGRUNNS_HENDELSER,
  HENDELSER_PER_DAG,
  SPILLDAG_MINUTTER,
  type BakgrunnsEffekt,
  type BakgrunnsHendelseDef,
  type BakgrunnsKontekst,
} from '@/data/events';

/**
 * Bakgrunnsmotoren (fase 4, Mad TV-mekanikk): lytter på tidsbruk og
 * trigger hendelser som skjer mens spilleren er opptatt et annet sted.
 * Definisjoner, vekting og betingelser bor i data/events.ts — motoren
 * her er ren mekanikk: trigger-rull, vektet valg, cooldown, dagslogg
 * og beskjedlapper til resepsjonen.
 *
 * Loggen lever i minnet for inneværende økt (MVP) — dagsoppsummeringen
 * leser den før dagen rulles.
 */

export interface BakgrunnsOppfoering {
  dag: number;
  minutt: number;
  hendelseId: string;
  tittel: string;
  oppsummering: string;
  beskjedlapp?: string;
  effekt?: BakgrunnsEffekt;
}

export class BakgrunnsMotor {
  private dagslogg: BakgrunnsOppfoering[] = [];
  private beskjeder: BakgrunnsOppfoering[] = [];
  private sistTrigget = new Map<string, number>();

  constructor(private rng: () => number = Math.random) {}

  /**
   * Kalles for hvert tidsforbruk. Sannsynligheten skalerer med antall
   * forbrukte minutter slik at forventningen blir ~HENDELSER_PER_DAG
   * per spilldag uansett hvor store tidshopp som gjøres.
   */
  haandterTid(minutter: number, ctx: BakgrunnsKontekst): BakgrunnsOppfoering | null {
    if (minutter <= 0) return null;
    const sannsynlighet = (minutter / SPILLDAG_MINUTTER) * HENDELSER_PER_DAG;
    if (this.rng() >= sannsynlighet) return null;

    const kandidater = BAKGRUNNS_HENDELSER.filter((h) => this.kanTrigges(h, ctx));
    if (kandidater.length === 0) return null;

    const valgt = this.vektetValg(kandidater, ctx);
    this.sistTrigget.set(valgt.id, ctx.dag);
    const oppforing: BakgrunnsOppfoering = {
      dag: ctx.dag,
      minutt: ctx.minutt,
      hendelseId: valgt.id,
      tittel: valgt.tittel,
      oppsummering: valgt.oppsummering,
      beskjedlapp: valgt.beskjedlapp,
      effekt: valgt.effekt,
    };
    this.dagslogg.push(oppforing);
    return oppforing;
  }

  private kanTrigges(h: BakgrunnsHendelseDef, ctx: BakgrunnsKontekst): boolean {
    const sist = this.sistTrigget.get(h.id);
    if (sist !== undefined && ctx.dag - sist < h.cooldownDager) return false;
    if (h.betingelse && !h.betingelse(ctx)) return false;
    return true;
  }

  /** Effektiv vekt: rival-hendelser skaleres med hevn-/spionasje-faktoren (5C). */
  private effektivVekt(h: BakgrunnsHendelseDef, ctx: BakgrunnsKontekst): number {
    return h.kategori === 'rival' ? h.vekt * ctx.rivalVektFaktor : h.vekt;
  }

  private vektetValg(
    kandidater: BakgrunnsHendelseDef[],
    ctx: BakgrunnsKontekst,
  ): BakgrunnsHendelseDef {
    const total = kandidater.reduce((sum, h) => sum + this.effektivVekt(h, ctx), 0);
    let kule = this.rng() * total;
    for (const h of kandidater) {
      kule -= this.effektivVekt(h, ctx);
      if (kule < 0) return h;
    }
    return kandidater[kandidater.length - 1];
  }

  /** Hendelsene som har skjedd i dag — til dagsoppsummeringen. */
  dagensLogg(): readonly BakgrunnsOppfoering[] {
    return this.dagslogg;
  }

  /** Ruller dagen: lapper fra i dag legges i resepsjonsbunken. */
  nyDag(): void {
    for (const o of this.dagslogg) {
      if (o.beskjedlapp) this.beskjeder.push(o);
    }
    this.dagslogg = [];
  }

  /** Uleste beskjedlapper i resepsjonen. */
  ulesteBeskjeder(): readonly BakgrunnsOppfoering[] {
    return this.beskjeder;
  }

  markerBeskjederLest(): void {
    this.beskjeder = [];
  }

  // ---- Persistens ----

  tilLagring(): BakgrunnsMotorLagring {
    return {
      dagslogg: [...this.dagslogg],
      beskjeder: [...this.beskjeder],
      sistTrigget: Object.fromEntries(this.sistTrigget),
    };
  }

  /** Gjeninnlaster tilstand. Dagsloggen gjelder bare samme spilldag. */
  lastFraLagring(lagret: Partial<BakgrunnsMotorLagring>, sammeDag: boolean): void {
    this.sistTrigget = new Map(
      Object.entries(lagret.sistTrigget ?? {}).filter(
        ([, v]) => typeof v === 'number',
      ) as Array<[string, number]>,
    );
    this.beskjeder = Array.isArray(lagret.beskjeder) ? [...lagret.beskjeder] : [];
    this.dagslogg =
      sammeDag && Array.isArray(lagret.dagslogg) ? [...lagret.dagslogg] : [];
  }
}

export interface BakgrunnsMotorLagring {
  dagslogg: BakgrunnsOppfoering[];
  beskjeder: BakgrunnsOppfoering[];
  sistTrigget: Record<string, number>;
}

/** Delt motor-instans for spilløkta. */
export const bakgrunnsMotor = new BakgrunnsMotor();
